import { useEffect, useState } from "react";
import { getMAWorkView, type MAWorkCount, type MAWorkViewResponse, type MAWorkView, type MAWorkSelection } from "@/api/enrichment";

type View = MAWorkView;
const labels: Partial<Record<View, string>> = { WORK: "Admitted Work", MANUAL_EXCEPTION: "Manual admissions", NEEDS_REVIEW: "Needs review", OUTSIDE_MANDATE: "Outside mandate", BLOCKED: "Commercial blocks" };
const reasons: Record<string, string> = {
  protected_status: "Record has left the Work entry stage",
  cessation: "Cessation is recorded",
  archived: "Archived record",
  client: "Existing client",
  recorded_exclusion: "Recorded structural exclusion",
  opposition: "Explicit opposition is recorded",
  engaged_history: "Historical commercial event; current status and context need review",
  naf_excluded: "Industry falls outside the mandate",
  geography_excluded: "Headquarters falls outside the mandate",
  age_outside: "Retained director is below the mandate age",
  headcount_outside: "Employee count falls outside the mandate",
  revenue_outside: "Revenue falls outside the mandate",
  net_result_outside: "Net income falls outside the mandate",
  headcount_unproven: "Employee count or original band needs evidence",
  headcount_overlapping: "Employee band overlaps the mandate boundary",
  director_unresolved: "Retained director needs identification",
  age_unknown: "Retained director's age is unknown",
  age_conflict: "Director age records disagree",
  naf_unknown: "Industry code is missing or invalid",
  geography_unknown: "Headquarters location needs confirmation",
  financials_unknown: "Required financial amounts are missing",
  financial_period_unproven: "Financial reporting period needs confirmation",
  manual_scope_unknown: "Historical admission scope needs review",
  previous_success: "Previous enrichment success is recorded",
  previous_result_unknown: "Previous enrichment outcome is undetermined",
  ownership_to_confirm: "Ownership to confirm",
  score_below_threshold: "Stored score is below the configured threshold",
};

export function MAWorkSummary({ counts }: { counts: Record<string, MAWorkCount> }) {
  return <div className="grid gap-3 sm:grid-cols-3">{(Object.keys(labels) as View[]).map(view => {
    const c = counts[view];
    return <div key={view} className="rounded-xl bg-slate-50 p-3"><h3 className="font-medium">{labels[view]}</h3>
      <p>{c?.total ?? 0} records · {c?.reserved ?? 0} reserved</p>
      <p>{c?.eligible ?? 0} eligible · {c?.available ?? 0} available</p>
    </div>;
  })}</div>;
}

export function MAWorkSelectionPanel({ slug, counts, view, onView }: { slug: string; counts: Record<string, MAWorkCount>; view: View; onView: (view: View) => void }) {
  const [data, setData] = useState<MAWorkViewResponse | null>(null);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  useEffect(() => { let alive = true; setData(null); setError(false);
    getMAWorkView(slug, view, page).then(res => { if (alive) setData(res); }).catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [slug, view, page]);
  return <section className="space-y-3 rounded-plane bg-white p-4 shadow-plane" aria-label="M&A Work selection">
    <MAWorkSummary counts={counts} />
    <div className="flex gap-3" role="group" aria-label="Work views">{(Object.keys(labels) as View[]).map(v => <button key={v} type="button" aria-pressed={view === v} className="rounded-lg border px-3 py-2" onClick={() => { setPage(1); onView(v); }}>{labels[v]}</button>)}</div>
    <p className="text-sm text-slate-500">Admitted records remain workable despite mandate or score diagnostics. Ownership remains to be confirmed. Commercial blocks prevent distribution. Diagnostic and manual-admission views overlap; do not add their counts.</p>
    {error ? <p role="alert">Could not load this view. Check your access and try again.</p> : !data ? <p>Loading selection…</p> : <>
      <p>{data.total} records in this view</p>
      {data.items.length === 0 ? <p>No records in this view.</p> : <ul className="divide-y">{data.items.map(item => <li key={item.company_id} className="py-3">
        <span className="font-medium">{item.name}</span> · {item.selection.reserved ? "Reserved" : item.selection.available ? "Available" : "Not available for distribution"}
        <ul className="text-sm text-slate-500">{item.selection.reasons.map(reason => <li key={reason}>{reasons[reason] ?? reason.replace(/_/g, " ")}</li>)}</ul>
        {item.selection.manual_exception !== "none" && <p className="text-sm">Admission: {item.selection.manual_exception.replace(/_/g, " ")}</p>}
      </li>)}</ul>}
      <div className="flex gap-3"><button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button><span>Page {page}</span><button disabled={page * data.page_size >= data.total} onClick={() => setPage(p => p + 1)}>Next</button></div>
    </>}
  </section>;
}


export function MAWorkDiagnostic({ selection }: { selection: MAWorkSelection }) {
  return <aside className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm" aria-label="Work admission and mandate diagnostic">
    <p>{selection.eligible ? "Admitted for Work" : "Distribution blocked"} · Mandate: {selection.observable_mandate === "unknown" ? "needs verification" : selection.observable_mandate === "outside" ? "outside observed criteria" : "observable criteria compatible"}</p>
    <p>Ownership remains to be confirmed. This is not a certification of the full mandate.</p>
    <ul>{selection.reasons.map(reason => <li key={reason}>{reasons[reason] ?? reason.replace(/_/g, " ")}</li>)}</ul>
    {selection.manual_exception !== "none" && <p>Admission: {selection.manual_exception.replace(/_/g, " ")}</p>}
  </aside>;
}
